import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import {
  endOfWeek,
  startOfWeek,
  subMonths,
  isBefore,
  isAfter,
  isSameDay,
  startOfDay,
  endOfDay,
  format as formatDate
} from 'date-fns'

// Helper functions for isSameOrBefore and isSameOrAfter
const isSameOrBefore = (date1, date2) => {
  return isBefore(date1, date2) || isSameDay(date1, date2)
}

const isSameOrAfter = (date1, date2) => {
  return isAfter(date1, date2) || isSameDay(date1, date2)
}
import { formatNumber } from '../../../utils/Helper'
import TimelinePostTooltip from './TimelinePostTooltip'
import Post from './Post'

/**
 * PostsTimeline - Újrafelhasználható timeline komponens
 *
 * @param {Object} props
 * @param {Array} props.posts - A postok tömbje
 * @param {string} props.groupBy - 'authentication' vagy 'brand' - hogyan csoportosítsuk a postokat
 * @param {boolean} props.loading - Loading állapot
 * @param {string} props.activeProvider - Aktív provider szűrő (opcionális)
 * @param {Function} props.onGroupToggle - Callback amikor egy csoport ki/becsukódik
 * @param {Object} props.groupHeaderRenderer - Custom renderer a csoport header-hez
 * @param {Object} props.groupItemRenderer - Custom renderer a csoport elemeihez
 * @param {boolean} props.showListView - Megjelenítse-e a collapsed list view-t (default: true)
 */
export default function PostsTimeline({
  posts = [],
  groupBy = 'authentication', // 'authentication' vagy 'brand'
  loading = false,
  activeProvider = null,
  onGroupToggle = null,
  groupHeaderRenderer = null,
  groupItemRenderer = null,
  showListView = true,
  disableBrandNavigation = false // Ha true, a brand linkek nem navigálnak
}) {
  const [expandedGroups, setExpandedGroups] = useState({})
  const [hoveredPost, setHoveredPost] = useState(null) // { id, post, x, y }
  const hideTooltipTimeoutRef = useRef(null)
  const timelineScrollRef = useRef(null)

  const dayWidth = 12 // Fix nap szélesség

  // Csoportosítás a groupBy alapján
  const groups = useMemo(() => {
    const groupsMap = {}

    const filteredPosts = activeProvider
      ? posts.filter((post) => post.authentication?.provider === activeProvider)
      : posts

    filteredPosts.forEach((post) => {
      let groupKey
      let groupData

      if (groupBy === 'authentication') {
        const auth = post.authentication
        if (!auth) return
        groupKey = auth.id
        if (!groupsMap[groupKey]) {
          groupsMap[groupKey] = {
            key: groupKey,
            authentication: auth,
            posts: []
          }
        }
        groupData = groupsMap[groupKey]
      } else if (groupBy === 'brand') {
        const brands = post.brands && post.brands.length > 0
          ? post.brands
          : [{
              id: `unknown-${post.id}`,
              name: 'Unknown brand',
              domain: '',
              logo_url: null
            }]

        brands.forEach(brand => {
          groupKey = brand.id || brand.uuid || brand.name
          if (!groupsMap[groupKey]) {
            groupsMap[groupKey] = {
              key: groupKey,
              brand: brand,
              posts: []
            }
          }
          groupsMap[groupKey].posts.push(post)
        })
        return // brands.forEach miatt nem kell tovább
      }

      if (groupData) {
        groupData.posts.push(post)
      }
    })

    const sortedGroups = Object.values(groupsMap).sort((a, b) => {
      const dateA = a.posts[0]?.posted_at ? new Date(a.posts[0].posted_at) : new Date(0)
      const dateB = b.posts[0]?.posted_at ? new Date(b.posts[0].posted_at) : new Date(0)
      return dateB - dateA
    })

    return sortedGroups
  }, [posts, activeProvider, groupBy])

  const totalPosts = groups.reduce((sum, group) => sum + group.posts.length, 0)

  // Timeline generálás:
  // - az IDŐSZAK VÉGE a mai nap hetének utolsó napja
  // - a startDate az ELSŐ (legrégebbi) post hete (ha nincs post: 4 hónappal ezelőtti hét eleje)
  // - a megjelenített postokat NEM filterezzük, a rács a legrégebbi post hetétől a mai nap hetének utolsó napjáig tart
  // - a 16 x 7 = 112 nap CSAK a dayWidth (egyszerre látható napok száma) kiszámításához kell
  const timelineData = useMemo(() => {
    // Időszak vége: mai nap hetének utolsó napja
    const endDate = endOfWeek(new Date(), { weekStartsOn: 1 })

    // Kiszámoljuk a legrégebbi post dátumát
    let oldestPostDate = null
    posts.forEach(post => {
      if (post.posted_at) {
        const postDate = new Date(post.posted_at)
        if (!oldestPostDate || isBefore(postDate, oldestPostDate)) {
          oldestPostDate = postDate
        }
      }
    })

    // Ha nincs post, induljunk 4 hónappal ezelőttről
    if (!oldestPostDate) {
      oldestPostDate = subMonths(new Date(), 4)
    }

    // Időszak eleje: legrégebbi post hetének eleje
    const startDate = startOfWeek(oldestPostDate, { weekStartsOn: 1 })

    const days = []
    const weeksMap = new Map()
    const monthsMap = new Map()
    const currentDate = new Date(startDate)

    while (isSameOrBefore(currentDate, endDate)) {
      const dayData = {
        date: new Date(currentDate),
        day: currentDate.getDate(),
        dayOfWeek: currentDate.getDay(),
        monthKey: formatDate(currentDate, 'yyyy-MM'),
        monthName: formatDate(currentDate, 'MMMM')
      }

      days.push(dayData)

      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 })
      const weekKey = formatDate(weekStart, 'yyyy-MM-dd')

      if (!weeksMap.has(weekKey)) {
        weeksMap.set(weekKey, {
          key: weekKey,
          start: new Date(weekStart),
          end: endOfWeek(weekStart, { weekStartsOn: 1 }),
          startIndex: days.length,
          days: []
        })
      }
      weeksMap.get(weekKey).days.push(dayData)

      const monthKey = formatDate(currentDate, 'yyyy-MM')
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          key: monthKey,
          name: formatDate(currentDate, 'MMMM'),
          startIndex: days.length,
          dayCount: 0
        })
      }
      monthsMap.get(monthKey).dayCount++

      currentDate.setDate(currentDate.getDate() + 1)
    }

    const weeks = Array.from(weeksMap.values()).sort((a, b) => a.start.getTime() - b.start.getTime())
    const months = Array.from(monthsMap.values())

    return { days, weeks, months, startDate, endDate }
  }, [posts])

  // Szűrjük a csoportokat, hogy csak azokat jelenítsük meg, amelyeknek van postja a timeline időtartamán belül
  const groupsInTimeline = useMemo(() => {
    const { startDate, endDate } = timelineData
    return groups.filter(group => {
      // Ellenőrizzük, hogy van-e legalább egy post, ami a timeline időtartamán belül van
      return group.posts.some(post => {
        if (!post.posted_at) return false
        const postDate = new Date(post.posted_at)
        return isSameOrAfter(postDate, startDate) && isSameOrBefore(postDate, endDate)
      })
    })
  }, [groups, timelineData])

  useEffect(() => {
    if (groupsInTimeline.length && Object.keys(expandedGroups).length === 0) {
      const initial = groupsInTimeline.reduce((acc, group, index) => {
        acc[group.key] = index === 0
        return acc
      }, {})
      setExpandedGroups(initial)
    }
  }, [groupsInTimeline.length])


  // Alapértelmezetten a legújabb posthoz scrollozás (vagy a végére, ha nincs post)
  useEffect(() => {
    if (!timelineScrollRef.current || timelineData.days.length === 0 || groupsInTimeline.length === 0) {
      return
    }

    const scrollContainer = timelineScrollRef.current
    const expectedGridWidth = timelineData.days.length * dayWidth
    let hasScrolled = false

    // Megkeressük a legújabb post dátumát
    let newestPostDate = null
    posts.forEach(post => {
      if (post.posted_at) {
        const postDate = new Date(post.posted_at)
        if (!newestPostDate || isAfter(postDate, newestPostDate)) {
          newestPostDate = postDate
        }
      }
    })

    const scrollToNewestPost = () => {
      if (!scrollContainer || hasScrolled) return

      const scrollWidth = scrollContainer.scrollWidth
      const clientWidth = scrollContainer.clientWidth
      const currentScrollLeft = scrollContainer.scrollLeft

      // Ellenőrizzük, hogy a grid teljesen renderelődött-e
      // A scrollWidth-nek legalább a várt grid szélesség 80%-ának kell lennie
      if (scrollWidth < expectedGridWidth * 0.8) {
        return false // Még nincs kész
      }

      // Csak akkor scrollozunk, ha van scrollWidth és clientWidth, és a scrollWidth nagyobb
      if (scrollWidth > 0 && clientWidth > 0 && scrollWidth > clientWidth) {
        let targetScrollLeft = scrollWidth - clientWidth // Alapértelmezett: végére

        // Ha van legújabb post, akkor ellenőrizzük, hogy az utolsó 4 hónapon belül van-e
        if (newestPostDate) {
          const fourMonthsAgo = subMonths(new Date(), 4)
          const isWithinLastFourMonths = isAfter(newestPostDate, fourMonthsAgo) || isSameDay(newestPostDate, fourMonthsAgo)

          if (isWithinLastFourMonths) {
            // Ha az utolsó 4 hónapon belül van, akkor a végére görgetünk
          } else {
            // Ha régebbi, mint 4 hónappal ezelőtt, akkor a post dátumához görgetünk
            const postDayIndex = timelineData.days.findIndex(day => {
              const dayDate = day.date instanceof Date ? day.date : new Date(day.date)
              return isSameDay(dayDate, newestPostDate)
            })

            if (postDayIndex >= 0) {
              // Kiszámoljuk a post napjának pozícióját
              const postDayPosition = postDayIndex * dayWidth
              // A post napját a viewport közepére vagy jobb oldalára igazítjuk
              // De ne menjünk túl a végén
              targetScrollLeft = Math.min(
                postDayPosition - clientWidth / 2 + dayWidth / 2, // Közepére igazítva
                scrollWidth - clientWidth // Végére korlátozva
              )
              // Ne menjünk negatívba
              targetScrollLeft = Math.max(0, targetScrollLeft)
            }
          }
        }

        scrollContainer.scrollLeft = targetScrollLeft

        // Ellenőrizzük, hogy tényleg scrollozott-e
        if (Math.abs(scrollContainer.scrollLeft - targetScrollLeft) < 10 || scrollContainer.scrollLeft !== currentScrollLeft) {
          hasScrolled = true
          return true // Sikeres scrollozás
        }
      }

      // Ha még nincs clientWidth, de van scrollWidth, próbáljuk meg a scrollWidth-re scrollozni
      if (scrollWidth > 0 && scrollWidth > 100) {
        scrollContainer.scrollLeft = scrollWidth
        hasScrolled = true
        return true
      }

      return false
    }

    const timeouts = []

    const tryScroll = () => {
      if (hasScrolled) return
      scrollToNewestPost()
    }

    // ResizeObserver, hogy figyelje a container méret változásait
    const resizeObserver = new ResizeObserver(() => {
      if (!hasScrolled) {
        tryScroll()
      }
    })

    if (scrollContainer) {
      resizeObserver.observe(scrollContainer)
    }

    // MutationObserver, hogy figyelje a DOM változásait
    const mutationObserver = new MutationObserver(() => {
      if (!hasScrolled) {
        tryScroll()
      }
    })

    if (scrollContainer) {
      mutationObserver.observe(scrollContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      })
    }

    // Kezdjük el a scrollozást - több időpontban próbáljuk meg
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tryScroll()
        timeouts.push(setTimeout(tryScroll, 50))
        timeouts.push(setTimeout(tryScroll, 100))
        timeouts.push(setTimeout(tryScroll, 200))
        timeouts.push(setTimeout(tryScroll, 300))
        timeouts.push(setTimeout(tryScroll, 500))
        timeouts.push(setTimeout(tryScroll, 750))
        timeouts.push(setTimeout(tryScroll, 1000))
        timeouts.push(setTimeout(tryScroll, 1500))
        timeouts.push(setTimeout(tryScroll, 2000))
        timeouts.push(setTimeout(tryScroll, 3000))
        timeouts.push(setTimeout(tryScroll, 4000))
      })
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      timeouts.forEach(clearTimeout)
    }
  }, [timelineData.days.length, groupsInTimeline.length, timelineData.endDate, posts.length])

  const toggleGroup = (key) => {
    const newExpanded = { ...expandedGroups, [key]: !expandedGroups[key] }
    setExpandedGroups(newExpanded)
    if (onGroupToggle) {
      onGroupToggle(key, newExpanded[key])
    }
  }

  const getDayIndexForDate = useCallback((date) => {
    if (!date || !timelineData.days.length) return 0
    const postDate = new Date(date)
    const { startDate, days, endDate } = timelineData

    if (isBefore(postDate, startDate)) return 0
    if (isAfter(postDate, endDate)) return days.length - 1

    const dayIndex = days.findIndex(d => {
      const dayDate = d.date instanceof Date ? d.date : new Date(d.date)
      return isSameDay(dayDate, postDate)
    })
    return dayIndex >= 0 ? dayIndex : 0
  }, [timelineData])

  const getWeekRange = (date) => {
    const weekStart = startOfWeek(new Date(date), { weekStartsOn: 1 })
    const weekEnd = endOfWeek(new Date(date), { weekStartsOn: 1 })
    return `${formatDate(weekStart, 'dd')} - ${formatDate(weekEnd, 'dd')}`
  }

  // Színpaletta minden csoportnak
  const colorPalette = [
    '#10B981', // rb-green (zöld)
    '#3B82F6', // kék
    '#8B5CF6', // lila
    '#F59E0B', // narancs
    '#EF4444', // piros
    '#06B6D4', // cián
    '#EC4899', // pink
    '#84CC16', // lime
    '#F97316', // narancs-sötét
    '#6366F1', // indigo
    '#14B8A6', // teal (rgb(20, 184, 166))
    '#A855F7', // purple
    '#22C55E', // zöld-világos
    '#F43F5E', // rózsaszín
    '#0EA5E9', // kék-világos
    '#64748B', // szürke-kék
  ]

  // Szín kiválasztása csoport kulcs alapján
  const getGroupColor = (groupKey) => {
    // Hash alapú szín választás a kulcsból
    let hash = 0
    const keyString = String(groupKey)
    for (let i = 0; i < keyString.length; i++) {
      hash = keyString.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % colorPalette.length
    return colorPalette[index]
  }

  // Default group header renderer
  const renderGroupHeader = (group) => {
    if (groupHeaderRenderer) {
      return groupHeaderRenderer(group)
    }

    if (groupBy === 'authentication') {
      const auth = group.authentication
      const avatarUrl = typeof auth.avatar === 'string' ? auth.avatar : auth.avatar?.url
      const authLink = `/project_manager/authentications/${auth.id}/influencer/collaborations`

      return (
        <a
          href={authLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 min-w-0 flex-1 hover:opacity-80 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={auth.username || 'Avatar'}
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none'
                const fallback = e.target.nextElementSibling
                if (fallback) fallback.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className="h-8 w-8 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter flex items-center justify-center text-xs font-semibold text-rb-lighter-grey dark:text-gray-400 flex-shrink-0"
            style={{ display: avatarUrl ? 'none' : 'flex' }}
          >
            {auth.username?.slice(0, 2)?.toUpperCase() || 'AU'}
          </div>
          <span className="text-sm font-medium truncate dark:text-white min-w-0">
            @{auth.username}
          </span>
        </a>
      )
    } else if (groupBy === 'brand') {
      const brand = group.brand
      // Használjuk az id-t vagy uuid-t, ha van
      const brandId = brand.id || brand.uuid
      const brandLink = brandId ? `/project_manager/brands/${brandId}` : '#'

      const brandContent = (
        <>
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.name || 'Brand logo'}
              className="h-8 w-8 rounded-md object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-md bg-red-100 dark:bg-rb-dark-mode-lighter flex items-center justify-center text-xs font-bold text-red-600 dark:text-gray-400 flex-shrink-0">
              {brand.name?.slice(0, 2)?.toUpperCase() || 'BR'}
            </div>
          )}
          <span className="text-sm font-medium truncate dark:text-white min-w-0">
            {brand.name || 'Unknown brand'}
          </span>
        </>
      )

      if (disableBrandNavigation) {
        return (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {brandContent}
          </div>
        )
      }

      return (
        <a
          href={brandLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 min-w-0 flex-1 hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation()
            if (!brandId) {
              e.preventDefault()
            }
          }}
        >
          {brandContent}
        </a>
      )
    }
  }

  // Default group item renderer (collapsed list)
  const renderGroupItem = (group) => {
    if (groupItemRenderer) {
      return groupItemRenderer(group)
    }

    const { posts: groupPosts } = group
    const expanded = expandedGroups[group.key]
    const authentication = groupBy === 'authentication' ? group.authentication : groupPosts[0]?.authentication

    return (
      <div
        key={group.key}
        className="bg-white dark:bg-rb-dark-mode rounded-2xl border border-rb-widget-border dark:border-rb-dark-mode-lighter shadow-sm overflow-hidden"
      >
        <button
          className="w-full flex items-center justify-between px-5 py-4 text-left dark:text-white"
          onClick={() => toggleGroup(group.key)}
        >
          <div className="flex items-center gap-4 min-w-0">
            {groupBy === 'authentication' && authentication?.avatar ? (
              <img
                src={typeof authentication.avatar === 'string' ? authentication.avatar : authentication.avatar?.url}
                alt={authentication.username || 'Avatar'}
                className="h-12 w-12 rounded-xl object-cover flex-shrink-0"
              />
            ) : groupBy === 'brand' && group.brand?.logo_url ? (
              <img
                src={group.brand.logo_url}
                alt={group.brand.name}
                className="h-12 w-12 rounded-xl object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-rb-grey dark:bg-rb-dark-mode-lighter flex items-center justify-center text-sm font-semibold text-rb-lighter-grey dark:text-gray-400 flex-shrink-0">
                {groupBy === 'authentication'
                  ? authentication?.username?.slice(0, 2)?.toUpperCase() || 'AU'
                  : group.brand?.name?.slice(0, 2)?.toUpperCase() || 'BR'}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-lg font-semibold truncate dark:text-white">
                {groupBy === 'authentication'
                  ? `@${authentication?.username || 'Unknown auth'}`
                  : group.brand?.name || 'Unknown brand'}
              </p>
              <p className="text-xs text-rb-lighter-grey dark:text-gray-400">
                {groupPosts.length} sponsored posts
                {groupBy === 'authentication' && authentication?.reach && ` · reach ${formatNumber(authentication.reach)}`}
                {groupBy === 'brand' && group.brand?.domain && ` · ${group.brand.domain}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-rb-lighter-grey dark:text-gray-400">{groupPosts.length} posts</span>
            {expanded ? <ChevronUp className="dark:text-white" /> : <ChevronDown className="dark:text-white" />}
          </div>
        </button>

        {expanded && (
          <div className="border-t border-rb-widget-border dark:border-rb-dark-mode-lighter bg-rb-grey dark:bg-rb-dark-mode-background px-5 py-4">
            <div className="flex flex-wrap gap-4">
              {groupPosts.map(post => (
                <Post
                  key={post.id}
                  post={post}
                  reach={authentication?.reach}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <>
        <div className="h-24 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 border border-dashed border-gray-200 dark:border-rb-dark-mode-lighter rounded-md dark:bg-rb-dark-mode">
          Loading posts…
        </div>
        <TimelinePostTooltip
          hoveredPost={hoveredPost}
          onClose={() => setHoveredPost(null)}
          hideTooltipTimeoutRef={hideTooltipTimeoutRef}
          reach={hoveredPost?.post?.authentication?.reach}
        />
      </>
    )
  }

  if (!groupsInTimeline.length) {
    return (
      <>
        <div className="h-24 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 border border-dashed border-gray-200 dark:border-rb-dark-mode-lighter rounded-md dark:bg-rb-dark-mode">
          No sponsored posts found in the timeline period.
        </div>
        <TimelinePostTooltip
          hoveredPost={hoveredPost}
          onClose={() => setHoveredPost(null)}
          hideTooltipTimeoutRef={hideTooltipTimeoutRef}
          reach={hoveredPost?.post?.authentication?.reach}
        />
      </>
    )
  }

  return (
    <>
      <div>
      {/* Timeline grid */}
      <div className="bg-white dark:bg-rb-dark-mode rounded-lg shadow-sm border border-rb-widget-border dark:border-rb-dark-mode-lighter p-4 mb-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-sm font-semibold dark:text-white">Collaboration timeline</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Sponsored posts {groupBy === 'authentication' ? 'grouped by authentication' : 'grouped by brand'} over time.
            </p>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {totalPosts} sponsored posts · {groupsInTimeline.length} {groupBy === 'authentication' ? 'authentications' : 'brands'}
          </div>
        </div>

        <div
          id="timeline-scroll-container"
          ref={timelineScrollRef}
          className="border border-rb-widget-border overflow-auto"
          style={{ maxHeight: '600px' }}
        >
          {/* Teljes szélességű tartalom: első oszlop + grid, vízszintes scrollozással együtt */}
          <div
            className="flex flex-col"
            style={{ minWidth: `${200 + timelineData.days.length * dayWidth}px` }}
          >
            {/* Fix első két sor függőleges scrollozáskor (sticky header) */}
            <div
              className="sticky top-0 z-10 flex flex-col bg-white dark:bg-rb-dark-mode"
              style={{ boxShadow: '0 1px 0 0 var(--rb-widget-border, #e5e7eb)' }}
            >
              <div className="flex flex-shrink-0">
                {/* Üres cella a hónapok sorához - fix vízszintes scrollnál */}
                <div className="flex-shrink-0 sticky left-0 z-10 border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter bg-gray-50 dark:bg-rb-dark-mode-lighter w-[200px] h-8" />
                {/* Hónapok sor */}
                <div
                  className="grid border-l-0 flex-1 flex-shrink-0"
                  style={{
                    gridTemplateColumns: `repeat(${timelineData.days.length}, ${dayWidth}px)`,
                    minWidth: `${timelineData.days.length * dayWidth}px`
                  }}
                >
                  {timelineData.months.map((month) => (
                    <div
                      key={month.key}
                      className="border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter bg-gray-50 dark:bg-rb-dark-mode-lighter text-xs font-semibold px-2 py-1 text-center dark:text-white"
                      style={{
                        gridColumnStart: month.startIndex + 1,
                        gridColumnEnd: month.startIndex + month.dayCount + 1
                      }}
                    >
                      {month.name}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-shrink-0">
                {/* Üres cella a hetek sorához - fix vízszintes scrollnál */}
                <div className="flex-shrink-0 sticky left-0 z-10 border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter bg-gray-50 dark:bg-rb-dark-mode-lighter w-[200px] h-4" />
                {/* Hetek sor */}
                <div
                  className="grid border-l-0 flex-1 flex-shrink-0"
                  style={{
                    gridTemplateColumns: `repeat(${timelineData.days.length}, ${dayWidth}px)`,
                    minWidth: `${timelineData.days.length * dayWidth}px`
                  }}
                >
                  {timelineData.weeks.map((week) => {
                    const maxColumn = timelineData.days.length + 1
                    const startCol = week.startIndex + 1
                    const endCol = Math.min(week.startIndex + week.days.length + 1, maxColumn)
                    return (
                      <div
                        key={week.key}
                        className="border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter bg-gray-50 dark:bg-rb-dark-mode-lighter text-xs text-gray-500 dark:text-gray-400 px-1 py-1 text-center whitespace-nowrap overflow-hidden"
                        style={{
                          gridColumnStart: startCol,
                          gridColumnEnd: endCol
                        }}
                      >
                        {getWeekRange(week.start)}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Csoport sorok: bal oszlop + grid */}
            {groupsInTimeline.map((group) => {
              const sortedPosts = [...group.posts].sort((a, b) => {
                const dateA = a.posted_at ? new Date(a.posted_at) : new Date(0)
                const dateB = b.posted_at ? new Date(b.posted_at) : new Date(0)
                return dateA - dateB
              })

              const firstPost = sortedPosts[0]
              const lastPost = sortedPosts[sortedPosts.length - 1]
              const firstDate = firstPost?.posted_at
              const lastDate = lastPost?.posted_at

              const startDayIndex = getDayIndexForDate(firstDate)
              const endDayIndex = getDayIndexForDate(lastDate)
              const groupColor = getGroupColor(group.key)

              return (
                <div key={group.key} className="flex flex-shrink-0">
                  {/* Influencer/brand oszlop - fix vízszintes scrollnál */}
                  <div
                    className="flex-shrink-0 sticky left-0 z-[5] border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter flex items-center gap-2 px-2 bg-white dark:bg-rb-dark-mode w-[200px] overflow-hidden"
                    style={{ height: '40px' }}
                  >
                    {renderGroupHeader(group)}
                  </div>
                  <div
                    className="grid border-l-0 flex-shrink-0"
                    style={{
                      gridTemplateColumns: `repeat(${timelineData.days.length}, ${dayWidth}px)`,
                      minWidth: `${timelineData.days.length * dayWidth}px`
                    }}
                  >
                    {timelineData.days.map((day, dayIndex) => {
                      const dayDate = day.date

                      const dayDateObj = dayDate instanceof Date ? dayDate : new Date(dayDate)
                      const isInRange = firstDate && lastDate &&
                        isSameOrAfter(dayDateObj, startOfDay(new Date(firstDate))) &&
                        isSameOrBefore(dayDateObj, endOfDay(new Date(lastDate)))

                      const postOnThisDay = sortedPosts.find(post => {
                        if (!post.posted_at) return false
                        const postDate = new Date(post.posted_at)
                        return isSameDay(postDate, dayDateObj)
                      })
                      const hasPost = !!postOnThisDay

                      const isFirstDay = dayIndex === startDayIndex
                      const isLastDay = dayIndex === endDayIndex

                      return (
                        <div
                          key={dayIndex}
                          className="border-r border-b border-rb-widget-border dark:border-rb-dark-mode-lighter relative h-10 flex items-center justify-center bg-white dark:bg-rb-dark-mode"
                          style={{ gridColumn: dayIndex + 1 }}
                        >
                          {isInRange && (
                            <div
                              className="absolute"
                              style={{
                                left: isFirstDay ? '50%' : '0',
                                right: isLastDay ? '50%' : '0',
                                top: '50%',
                                height: '2px',
                                backgroundColor: groupColor,
                                transform: 'translateY(-50%)',
                                zIndex: 1
                              }}
                            />
                          )}

                          {hasPost && postOnThisDay && (
                            <div className="relative">
                              {/* Pont a timeline-on */}
                              <div
                                className="absolute h-3 w-3 rounded-full border-2 border-white shadow-sm cursor-pointer"
                                style={{
                                  top: '50%',
                                  left: '50%',
                                  backgroundColor: groupColor,
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 1
                                }}
                                onMouseEnter={(event) => {
                                  if (hideTooltipTimeoutRef.current) {
                                    clearTimeout(hideTooltipTimeoutRef.current)
                                    hideTooltipTimeoutRef.current = null
                                  }
                                  const rect = event.currentTarget.getBoundingClientRect()
                                  setHoveredPost({
                                    id: postOnThisDay.id,
                                    post: postOnThisDay,
                                    x: rect.left + rect.width / 2,
                                    y: rect.top
                                  })
                                }}
                                onMouseLeave={() => {
                                  if (hideTooltipTimeoutRef.current) {
                                    clearTimeout(hideTooltipTimeoutRef.current)
                                  }
                                  hideTooltipTimeoutRef.current = setTimeout(() => {
                                    setHoveredPost((current) =>
                                      current && current.id === postOnThisDay.id ? null : current
                                    )
                                    hideTooltipTimeoutRef.current = null
                                  }, 150)
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Collapsed list view */}
      {showListView && (
        <div className="space-y-4">
          {groupsInTimeline.map(group => renderGroupItem(group))}
        </div>
      )}
      </div>
      <TimelinePostTooltip
        hoveredPost={hoveredPost}
        onClose={() => setHoveredPost(null)}
        hideTooltipTimeoutRef={hideTooltipTimeoutRef}
        reach={hoveredPost?.post?.authentication?.reach}
      />
    </>
  )
}
